import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import stadiumBg from "../../assets/images/streamer-stadium-bg.jpg";
import mainLogo from "../../assets/images/main_logo.svg";
import { useQuery, useQueries } from "@tanstack/react-query";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdSearch, MdContentCopy } from "react-icons/md";
import toast from "react-hot-toast";
import { Api } from "../../services/interceptor";
import useDebounce from "../../hooks/useDebounce";
import "./StreamerDashboard.scss";
import { useAppSelector } from "../../redux/hooks";
import { NetworkQualityIndicator } from "../../components/common/NetworkQualityIndicator";
import { isTestMatch } from "../../helper/helper";

interface LeagueOption {
  leagueId: string;
  leagueName: string;
}

interface TournamentOption {
  tournamentId: string;
  tournamentName: string;
}

interface MatchOption {
  matchId: string;
  teamOne?: { teamName?: string; teamSnapshotId?: string };
  teamTwo?: { teamName?: string; teamSnapshotId?: string };
  matchStatus?: string;
  startDate?: string;
}

interface OverlayItem {
  display: string;
  internal: string;
  special?: "wicket" | "playerOut" | "message" | "nextMatch" | "battingScorecard" | "bowlingScorecard" | "playerStats" | "matchPlayerStats" | "pointsTable" | "inningChart" | "strategicTimeout" | "inningsBreak" | "reviewTaken" | "squadDetails" | "showTarget" | "drsTimer" | "winPrediction";
}

interface DivisionResponse {
  divisionId?: string;
  divisionName?: string;
  childDivisions?: DivisionResponse[];
  teams?: Array<{ teamSnapshotId?: string; teamName?: string }>;
}

interface TournamentDivisionsResponse {
  tournamentId?: string;
  divisionResponseList?: DivisionResponse[];
}

interface BattingScoreCardItem {
  playerId?: string;
  playerFirstName?: string;
  playerLastName?: string;
  run?: number;
  balls?: number;
  wicket?: { type?: string };
  outString?: string;
}

interface InningScorecard {
  inningId?: string;
  inningType?: string;
  inningStatus?: string;
  over?: number;
  ball?: number;
  battingTeamId?: string;
  bowlingTeamId?: string;
  battingTeamName?: string;
  bowlingTeamName?: string;
  battingScoreCardResponseList?: BattingScoreCardItem[];
}

interface MatchDetails {
  divisionId?: string;
  matchType?: string;
  inningScorecardList?: InningScorecard[];
  tournament?: { tournamentId?: string };
}

interface TeamPlayer {
  playerId?: string;
  playerFirstName?: string;
  playerLastName?: string;
}

interface TeamPlayersResponse {
  playingSquad?: TeamPlayer[];
}

interface TickerTheme {
  id: number;
  name: string;
  slug: string;
  active: boolean;
}

const MIN_OVERS_FOR_WIN_PREDICTION = 6;

const overlayGroups: Array<{ label: string; items: OverlayItem[] }> = [
  {
    label: "Live Preview (Bottom Ticker Overlays)",
    items: [
      { display: "Four", internal: "Four" },
      { display: "Six", internal: "Six" },
      { display: "Wicket", internal: "Wicket", special: "wicket" },
      { display: "Player Out", internal: "PlayerOut", special: "playerOut" },
      { display: "New Ball", internal: "NewBall" },
    ],
  },
  {
    label: "Match Events",
    items: [
      { display: "Tea Break", internal: "TeaBreak" },
      { display: "Lunch Break", internal: "LunchBreak" },
      { display: "Bad Light", internal: "BadLights" },
      { display: "Weather Info", internal: "WeatherInfo" },
      { display: "Show Match Message", internal: "HeaderMessage", special: "message" },
      { display: "Strategic Timeout", internal: "StrategicTimeout", special: "strategicTimeout" },
      { display: "Innings Break", internal: "InningBreak", special: "inningsBreak" },
      { display: "Review Taken", internal: "ReviewTaken", special: "reviewTaken" },
      { display: "Decision Pending", internal: "DecisionPending" },
      { display: "DRS Timer", internal: "DrsTimer", special: "drsTimer" },
    ],
  },
  {
    label: "Information",
    items: [
      { display: "Match Summary", internal: "DaySummary" },
      // { display: 'Inning End Summary', internal: 'InningEndSummary' },
      { display: "Toss Win Ratio", internal: "TossResult" },
      { display: "Preview Match", internal: "NextMatch", special: "nextMatch" },
      { display: "Win Prediction", internal: "WinPrediction", special: "winPrediction" },
      { display: "Current Partnership", internal: "CurrentPartnership" },
      { display: "Target", internal: "Target", special: "showTarget" },
      { display: "Squad Details", internal: "PlayingSquad", special: "squadDetails" },
    ],
  },
  {
    label: "Scorecards",
    items: [
      { display: "Batting Scorecard", internal: "BattingScoreCard", special: "battingScorecard" },
      { display: "Bowling Scorecard", internal: "BowlingScoreCard", special: "bowlingScorecard" },
      { display: "Player Stats", internal: "BatterStats", special: "playerStats" },
      { display: "Match Player Stats", internal: "MatchPlayerStats", special: "matchPlayerStats" },
    ],
  },
  {
    label: "Charts",
    items: [
      { display: "Worm Chart", internal: "InningsChart" },
      { display: "Manhattan Chart", internal: "RunsChart" },
      { display: "Team Partnership Chart", internal: "PartnershipChart", special: "inningChart" },
      { display: "Run Rate Chart", internal: "RunRateChart" },
      { display: "Batter Chart", internal: "BatterVsBatterChart", special: "inningChart" },
      { display: "Bowler Chart", internal: "BowlerVsBowlerChart", special: "inningChart" },
      { display: "Extras Chart", internal: "ExtrasChart", special: "inningChart" },
      { display: "Team Wagon Wheel", internal: "WagonWheel", special: "inningChart" },
      // { display: 'Boundary Ball %', internal: 'BoundaryBallPercentageChart' },
    ],
  },
  {
    label: "Top Stats",
    items: [
      { display: "Top 10 Batsman", internal: "Top10Batsman" },
      { display: "Top 10 Bowlers", internal: "Top10Bowlers" },
    ],
  },
  {
    label: "Tournament",
    items: [{ display: "Points Table", internal: "PointsTable", special: "pointsTable" }],
  },
];

const StreamerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const userProfile = useAppSelector((s) => s.selectedProfile.playerProfile);

  const [leagueSearch, setLeagueSearch] = useState("");
  const [leagueDropdownOpen, setLeagueDropdownOpen] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<LeagueOption | null>(null);

  const [tournamentDropdownOpen, setTournamentDropdownOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<TournamentOption | null>(null);

  const [matchDropdownOpen, setMatchDropdownOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchOption | null>(null);

  const [previewMode, setPreviewMode] = useState<"preview" | "live">("preview");
  const [previewId, setPreviewId] = useState<string>("");

  // const [directId, setDirectId] = useState('');
  // const [youtubeLink, setYoutubeLink] = useState('');

  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<TickerTheme | null>(null);
  const [initialTheme, setInitialTheme] = useState<TickerTheme | null>(null);
  const [themeChangeConfirmOpen, setThemeChangeConfirmOpen] = useState(false);
  const themeRef = useRef<HTMLDivElement>(null);

  // Dialog state
  const [playerStatsDialogOpen, setPlayerStatsDialogOpen] = useState(false);
  const [playerStatsCurrentMatch, setPlayerStatsCurrentMatch] = useState(false);
  const [playerStatsTab, setPlayerStatsTab] = useState<"batting" | "bowling">("batting");

  const [scorecardDialogOpen, setScorecardDialogOpen] = useState(false);
  const [scorecardType, setScorecardType] = useState<string>("BattingScoreCard");

  const [playerOutDialogOpen, setPlayerOutDialogOpen] = useState(false);

  const [wicketDialogOpen, setWicketDialogOpen] = useState(false);

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState("");

  const [pointsTableDialogOpen, setPointsTableDialogOpen] = useState(false);
  const [pointsTableTab, setPointsTableTab] = useState(0);

  const [reviewTakenDialogOpen, setReviewTakenDialogOpen] = useState(false);

  const [squadDialogOpen, setSquadDialogOpen] = useState(false);

  const [breakDurationDialogOpen, setBreakDurationDialogOpen] = useState(false);
  const [breakDurationType, setBreakDurationType] = useState<"StrategicTimeout" | "InningBreak" | "DrsTimer">("StrategicTimeout");
  const [breakDurationMinutes, setBreakDurationMinutes] = useState(0);
  const [breakDurationSeconds, setBreakDurationSeconds] = useState(0);

  const leagueRef = useRef<HTMLDivElement>(null);
  const tournamentRef = useRef<HTMLDivElement>(null);
  const matchRef = useRef<HTMLDivElement>(null);
  const previewWrapperRef = useRef<HTMLDivElement>(null);
  const [iframeScale, setIframeScale] = useState(0.313);

  useEffect(() => {
    const wrapper = previewWrapperRef.current;
    if (!wrapper) return;
    setIframeScale(wrapper.getBoundingClientRect().width / 1920);
    const observer = new ResizeObserver(([entry]) => {
      setIframeScale(entry.contentRect.width / 1920);
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [selectedMatch]);

  const debouncedLeagueSearch = useDebounce(leagueSearch, 400);

  const { data: leagueResults = [], isFetching: leagueLoading } = useQuery<LeagueOption[]>({
    queryKey: ["searchLeagueByName", debouncedLeagueSearch],
    queryFn: async () => {
      if (!debouncedLeagueSearch.trim()) return [];
      const res = await Api.get(`/searchLeagueByName?name=${encodeURIComponent(debouncedLeagueSearch)}`);
      return res.data;
    },
    enabled: !!debouncedLeagueSearch.trim(),
  });

  const { data: tournamentData } = useQuery({
    queryKey: ["getTournamentsByLeagueId", selectedLeague?.leagueId],
    queryFn: async () => {
      const res = await Api.get(`/getTournamentsByLeagueId/${selectedLeague!.leagueId}`);
      return res.data;
    },
    enabled: !!selectedLeague,
  });
  const tournaments: TournamentOption[] = tournamentData?.tournamentResponses ?? [];

  const matchQueries = useQueries({
    queries: (["IN_PROGRESS", "SCHEDULED", "ENDED"] as const).map((status) => ({
      queryKey: ["matchByTournamentIdAndStatus", selectedTournament?.tournamentId, status],
      queryFn: async () => {
        const res = await Api.get("/matchByTournamentIdAndStatus", {
          params: { tournamentId: selectedTournament!.tournamentId, status },
        });
        return res.data as { matchResponseList?: MatchOption[] };
      },
      enabled: !!selectedTournament,
    })),
  });
  const matches: MatchOption[] = matchQueries.flatMap((q) => q.data?.matchResponseList ?? []);

  const { data: matchDetails, refetch: refetchMatchDetails } = useQuery<MatchDetails>({
    queryKey: ["matchDetails", selectedMatch?.matchId],
    queryFn: async () => {
      const res = await Api.get(`/match/${selectedMatch!.matchId}`);
      return res.data;
    },
    enabled: !!selectedMatch,
  });

  const { data: matchTickerData } = useQuery<{ tickerTheme?: TickerTheme }>({
    queryKey: ["getMatchTicker", selectedMatch?.matchId],
    queryFn: async () => {
      const res = await Api.get(`/getMatchTicker/${selectedMatch!.matchId}`);
      return res.data;
    },
    enabled: !!selectedMatch,
  });

  useEffect(() => {
    if (matchTickerData?.tickerTheme) {
      setSelectedTheme(matchTickerData.tickerTheme);
      setInitialTheme(matchTickerData.tickerTheme);
    }
  }, [matchTickerData]);

  const handleGoLive = () => {
    setPreviewMode("live");
    if (selectedTheme && initialTheme && selectedTheme.id !== initialTheme.id) {
      setThemeChangeConfirmOpen(true);
    }
  };

  const handleConfirmThemeChangeForLive = async () => {
    if (selectedMatch && selectedTheme) {
      await Api.put("/match/updateMatchTickerTheme", {
        matchId: selectedMatch.matchId,
        showPreviewMode: false,
        tickerThemeId: selectedTheme.id,
        graphicsType: "ThemeChange",
      });
      setInitialTheme(selectedTheme);
    }
    setThemeChangeConfirmOpen(false);
  };

  const handleCancelThemeChangeForLive = () => {
    setSelectedTheme(initialTheme);
    setThemeChangeConfirmOpen(false);
  };

  const latestInning = matchDetails?.inningScorecardList?.at(-1);

  const { data: battingTeamPlayers } = useQuery<TeamPlayersResponse>({
    queryKey: ["teamSquad", selectedMatch?.matchId, latestInning?.battingTeamId],
    queryFn: async () => {
      const res = await Api.get(`/getTeamSquadForMatch/${selectedMatch!.matchId}/${latestInning!.battingTeamId}`);
      return res.data;
    },
    enabled: playerStatsDialogOpen && !!selectedMatch && !!latestInning?.battingTeamId,
  });

  const { data: bowlingTeamPlayers } = useQuery<TeamPlayersResponse>({
    queryKey: ["teamSquad", selectedMatch?.matchId, latestInning?.bowlingTeamId],
    queryFn: async () => {
      const res = await Api.get(`/getTeamSquadForMatch/${selectedMatch!.matchId}/${latestInning!.bowlingTeamId}`);
      return res.data;
    },
    enabled: playerStatsDialogOpen && !!selectedMatch && !!latestInning?.bowlingTeamId,
  });

  const { data: tickerThemes = [] } = useQuery<TickerTheme[]>({
    queryKey: ["getTickerThemes"],
    queryFn: async () => {
      const res = await Api.get("/match/getTickerThemes");
      return res.data;
    },
  });

  const tournamentId = matchDetails?.tournament?.tournamentId;
  const { data: divisionsData } = useQuery<TournamentDivisionsResponse>({
    queryKey: ["getDivisions", tournamentId],
    queryFn: async () => {
      const res = await Api.get(`/getDivisions/${tournamentId}`);
      return res.data;
    },
    enabled: pointsTableDialogOpen && !!tournamentId,
  });
  const childDivisions: DivisionResponse[] = divisionsData?.divisionResponseList?.[0]?.childDivisions ?? [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (leagueRef.current && !leagueRef.current.contains(e.target as Node)) {
        setLeagueDropdownOpen(false);
      }
      if (tournamentRef.current && !tournamentRef.current.contains(e.target as Node)) {
        setTournamentDropdownOpen(false);
      }
      if (matchRef.current && !matchRef.current.contains(e.target as Node)) {
        setMatchDropdownOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target as Node)) {
        setThemeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSetupAssets = () => {
    navigate("/streamer-dashboard/asset-setup", {
      state: {
        leagueId: selectedLeague!.leagueId,
        leagueName: selectedLeague!.leagueName,
        tournamentId: selectedTournament!.tournamentId,
        tournamentName: selectedTournament!.tournamentName,
      },
    });
  };

  const handleLeagueSelect = (league: LeagueOption) => {
    setSelectedLeague(league);
    setLeagueSearch("");
    setLeagueDropdownOpen(false);
    setSelectedTournament(null);
    setSelectedMatch(null);
  };

  const handleTournamentSelect = (tournament: TournamentOption) => {
    setSelectedTournament(tournament);
    setTournamentDropdownOpen(false);
    setSelectedMatch(null);
  };

  const handleMatchSelect = (match: MatchOption) => {
    setSelectedMatch(match);
    setPreviewId(crypto.randomUUID());
    setMatchDropdownOpen(false);
  };

  const getMatchLabel = (match: MatchOption) => `${match.teamOne?.teamName ?? "Team A"} vs ${match.teamTwo?.teamName ?? "Team B"}`;

  const [cacheKey, setCacheKey] = useState(() => Date.now());

  useEffect(() => {
    setCacheKey(Date.now());
  }, [selectedMatch, previewMode, previewId]);

  const iframeSrc = selectedMatch ? (previewMode === "preview" ? `${import.meta.env.VITE_TICKER_BASE_URL}preview/${selectedMatch.matchId}/${previewId}?t=${cacheKey}` : `${import.meta.env.VITE_TICKER_BASE_URL}match/${selectedMatch.matchId}?t=${cacheKey}`) : null;

  // const handleFetchMatch = () => {
  //   if (!directId.trim()) return;
  //   console.log('Fetching by ID:', directId);
  // };

  const postGraphics = async (params: { graphicsType: string; playerId?: string; divisionId?: string; inningId?: string; message?: string; tickerThemeId?: number; durationInSeconds?: number; teamName?: string; teamSnapShotId?: string; wicketType?: string; showTarget?: boolean }) => {
    if (!selectedMatch) return;
    const isPreview = previewMode === "preview";
    await Api.post("/showGraphics", {
      matchId: selectedMatch.matchId,
      showPreviewMode: isPreview,
      previewId: isPreview ? previewId : undefined,
      tickerThemeId: selectedTheme?.id,
      ...params,
    });
  };

  const handleInningRequiredChip = (type: string, freshDetails?: MatchDetails) => {
    const details = freshDetails ?? matchDetails;
    const innings = details?.inningScorecardList ?? [];
    if (innings.length <= 1) {
      postGraphics({ graphicsType: type, inningId: innings[0]?.inningId });
    } else {
      setScorecardType(type);
      setScorecardDialogOpen(true);
    }
  };

  const handleChipClick = async (item: OverlayItem) => {
    if (!selectedMatch) return;
    switch (item.special) {
      case "wicket":
        setWicketDialogOpen(true);
        break;
      case "playerOut": {
        await refetchMatchDetails();
        setPlayerOutDialogOpen(true);
        break;
      }
      case "message":
        setMessageDialogOpen(true);
        break;
      case "nextMatch":
        postGraphics({ graphicsType: "NextMatch", divisionId: matchDetails?.divisionId });
        break;
      case "battingScorecard": {
        const { data: freshDetails } = await refetchMatchDetails();
        handleInningRequiredChip("BattingScoreCard", freshDetails);
        break;
      }
      case "bowlingScorecard": {
        const { data: freshDetails } = await refetchMatchDetails();
        handleInningRequiredChip("BowlingScoreCard", freshDetails);
        break;
      }
      case "inningChart": {
        const { data: freshDetails } = await refetchMatchDetails();
        handleInningRequiredChip(item.internal, freshDetails);
        break;
      }
      case "playerStats": {
        await refetchMatchDetails();
        setPlayerStatsCurrentMatch(false);
        setPlayerStatsTab("batting");
        setPlayerStatsDialogOpen(true);
        break;
      }
      case "matchPlayerStats": {
        await refetchMatchDetails();
        setPlayerStatsCurrentMatch(true);
        setPlayerStatsTab("batting");
        setPlayerStatsDialogOpen(true);
        break;
      }
      case "pointsTable":
        setPointsTableTab(0);
        setPointsTableDialogOpen(true);
        break;
      case "squadDetails":
        if (selectedTheme?.slug === "bca-wpl") {
          setSquadDialogOpen(true);
        } else {
          postGraphics({ graphicsType: item.internal });
        }
        break;
      case "showTarget":
        postGraphics({ graphicsType: "Target", showTarget: true });
        break;
      case "winPrediction": {
        const { data: freshDetails } = await refetchMatchDetails();
        const details = freshDetails ?? matchDetails;
        if (isTestMatch(details?.matchType)) {
          toast.error("Win Prediction is only supported for T20 format");
          break;
        }
        const innings = details?.inningScorecardList ?? [];
        const latest = innings.at(-1);
        const secondLatest = innings.at(-2);
        // The reply innings of a super over is left as NORMAL by the backend
        // (only the first super-over innings is tagged SUPER_OVER), so also
        // check the innings right before the latest one.
        const isSuperOver = [latest, secondLatest].some((inning) => inning?.inningType?.toUpperCase() === "SUPER_OVER");
        if (latest && isSuperOver) {
          toast.error("Win Prediction is not supported for Super Over");
          break;
        }
        const isInningInProgress = latest?.inningStatus?.toUpperCase() === "STARTED";
        const oversCompleted = latest?.over ?? 0;
        if (isInningInProgress && oversCompleted < MIN_OVERS_FOR_WIN_PREDICTION) {
          toast.error(`Win Prediction is available once the first ${MIN_OVERS_FOR_WIN_PREDICTION} overs of the innings are completed`);
          break;
        }
        postGraphics({ graphicsType: "WinPrediction" });
        break;
      }
      case "drsTimer":
        setBreakDurationType("DrsTimer");
        setBreakDurationMinutes(0);
        setBreakDurationSeconds(0);
        setBreakDurationDialogOpen(true);
        break;
      case "reviewTaken":
        setReviewTakenDialogOpen(true);
        break;
      case "strategicTimeout":
        setBreakDurationType("StrategicTimeout");
        setBreakDurationMinutes(0);
        setBreakDurationSeconds(0);
        setBreakDurationDialogOpen(true);
        break;
      case "inningsBreak":
        setBreakDurationType("InningBreak");
        setBreakDurationMinutes(0);
        setBreakDurationSeconds(0);
        setBreakDurationDialogOpen(true);
        break;
      default:
        postGraphics({ graphicsType: item.internal });
    }
  };

  const outBatsmen = latestInning?.battingScoreCardResponseList?.map((b, idx) => ({ ...b, battingOrder: idx + 1 })).filter((b) => b.wicket != null) ?? [];

  const currentPlayers = playerStatsTab === "batting" ? (battingTeamPlayers?.playingSquad ?? []) : (bowlingTeamPlayers?.playingSquad ?? []);

  const getPlayerStatsType = (tab: "batting" | "bowling") => {
    if (tab === "batting") return playerStatsCurrentMatch ? "CurrentMatchBatterStats" : "BatterStats";
    return playerStatsCurrentMatch ? "CurrentMatchBowlerStats" : "BowlerStats";
  };

  if (userProfile?.streamerProfile == null) {
    return (
      <div className="streamer-dashboard streamer-dashboard--unauthorized">
        <div className="sd-unauthorized">
          <img src={mainLogo} alt="Cric Center" className="sd-unauthorized__logo" />
          <div className="sd-unauthorized__divider" />
          <h1 className="sd-unauthorized__title">Access Restricted</h1>
          <p className="sd-unauthorized__message">You are not authorized to view the Streamer Dashboard.</p>
          <p className="sd-unauthorized__hint">This area is available only to approved streamers. If you believe this is a mistake, please complete your streamer profile or contact the Cric Center team for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="streamer-dashboard">
      {/* ── Filter Bar ── */}
       {/* add a button with title setup assets */}
      <div className="sd-filter-bar">
        <div className="sd-filter-bar__options">
          {/* League */}
          <div className="sd-dropdown" ref={leagueRef}>
            <button type="button" className="sd-dropdown__trigger" onClick={() => setLeagueDropdownOpen((v) => !v)}>
              <span className={selectedLeague ? "sd-dropdown__value" : "sd-dropdown__placeholder"}>{selectedLeague ? selectedLeague.leagueName : "Select League"}</span>
              {leagueDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </button>

            {leagueDropdownOpen && (
              <div className="sd-dropdown__menu">
                <div className="sd-dropdown__search-row">
                  <MdSearch className="sd-dropdown__search-icon" />
                  <input autoFocus type="text" placeholder="Search league..." value={leagueSearch} onChange={(e) => setLeagueSearch(e.target.value)} className="sd-dropdown__search-input" />
                </div>
                <div className="sd-dropdown__list">
                  {leagueLoading && <p className="sd-dropdown__hint">Searching...</p>}
                  {!leagueLoading && debouncedLeagueSearch && leagueResults.length === 0 && <p className="sd-dropdown__hint">No leagues found</p>}
                  {!debouncedLeagueSearch && !leagueLoading && <p className="sd-dropdown__hint">Type to search leagues</p>}
                  {leagueResults.map((league) => (
                    <button key={league.leagueId} type="button" className="sd-dropdown__item" onClick={() => handleLeagueSelect(league)}>
                      {league.leagueName}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tournament */}
          <div className="sd-dropdown" ref={tournamentRef}>
            <button type="button" className={`sd-dropdown__trigger${!selectedLeague ? " sd-dropdown__trigger--disabled" : ""}`} onClick={() => selectedLeague && setTournamentDropdownOpen((v) => !v)}>
              <span className={selectedTournament ? "sd-dropdown__value" : "sd-dropdown__placeholder"}>{selectedTournament ? selectedTournament.tournamentName : "Select Tournaments"}</span>
              {tournamentDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </button>

            {tournamentDropdownOpen && (
              <div className="sd-dropdown__menu">
                <div className="sd-dropdown__list">
                  {tournaments.length === 0 ? (
                    <p className="sd-dropdown__hint">No tournaments found</p>
                  ) : (
                    tournaments.map((t) => (
                      <button key={t.tournamentId} type="button" className="sd-dropdown__item" onClick={() => handleTournamentSelect(t)}>
                        {t.tournamentName}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Match */}
          <div className="sd-dropdown" ref={matchRef}>
            <button type="button" className={`sd-dropdown__trigger${!selectedTournament ? " sd-dropdown__trigger--disabled" : ""}`} onClick={() => selectedTournament && setMatchDropdownOpen((v) => !v)}>
              <span className={selectedMatch ? "sd-dropdown__value" : "sd-dropdown__placeholder"}>{selectedMatch ? getMatchLabel(selectedMatch) : "Select Match"}</span>
              {matchDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </button>

            {matchDropdownOpen && (
              <div className="sd-dropdown__menu">
                <div className="sd-dropdown__list">
                  {matches.length === 0 ? (
                    <p className="sd-dropdown__hint">No matches found</p>
                  ) : (
                    matches.map((m) => (
                      <button key={m.matchId} type="button" className="sd-dropdown__item sd-dropdown__item--match" onClick={() => handleMatchSelect(m)}>
                        <div className="sd-dropdown__match-info">
                          <span className="sd-dropdown__match-teams">{getMatchLabel(m)}</span>
                          {m.startDate && (
                            <span className="sd-dropdown__match-date">
                              {new Date(m.startDate).toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                          )}
                        </div>
                        {m.matchStatus && <span className="sd-dropdown__item-badge">{m.matchStatus}</span>}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`sd-btn ${!selectedLeague || !selectedTournament ? "sd-btn--preview" : "sd-btn--active"}`}
            onClick={handleSetupAssets}
            disabled={!selectedLeague || !selectedTournament}
          >
            Setup Assets
          </button>

          {/* {/* Direct ID input */}
          {/* <div className="sd-direct-input">
            <input
              type="text"
              placeholder="Paste Match Id / Tournament Id"
              value={directId}
              onChange={e => setDirectId(e.target.value)}
              className="sd-direct-input__field"
            />
            <button
              type="button"
              className="sd-direct-input__btn"
              onClick={handleFetchMatch}
            >
              Fetch Match
            </button>
          </div>  */}

          {/* ── Network Quality Indicator ── */}
          <div className="sd-filter-bar__network">
            <NetworkQualityIndicator />
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="sd-content">
        {/* Left Panel */}
        <div className="sd-panel sd-panel--left">
          {/* Bottom Ticker Theme header */}
          <div className="sd-section-header">
            <span className="sd-section-header__title">Bottom Ticker Theme</span>
            <div className="sd-dropdown sd-dropdown--theme" ref={themeRef}>
              <button type="button" className="sd-theme-selector" onClick={() => setThemeDropdownOpen((v) => !v)}>
                {selectedTheme ? selectedTheme.name : "Select Theme"}
                {themeDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
              </button>
              {themeDropdownOpen && (
                <div className="sd-dropdown__menu">
                  <div className="sd-dropdown__list">
                    {tickerThemes
                      .filter((t) => t.active)
                      .map((theme) => (
                        <button
                          key={theme.id}
                          type="button"
                          className="sd-dropdown__item"
                          onClick={async () => {
                            setSelectedTheme(theme);
                            setThemeDropdownOpen(false);
                            if (selectedMatch) {
                              const isPreview = previewMode === "preview";
                              await Api.put("/match/updateMatchTickerTheme", {
                                matchId: selectedMatch.matchId,
                                showPreviewMode: isPreview,
                                previewId: isPreview ? previewId : undefined,
                                tickerThemeId: theme.id,
                                graphicsType: "ThemeChange",
                              });
                              if (!isPreview) {
                                setInitialTheme(theme);
                              }
                            }
                          }}
                        >
                          {theme.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Ticker preview bar */}
          {/* <div className="sd-ticker-preview">
            <span className="sd-ticker-preview__placeholder">Score ticker preview</span>
          </div>  */}

          {/* Overlays header */}
          <div className="sd-overlays-header">
            <span className="sd-overlays-header__title">Overlays</span>
          </div>

          {/* Overlay groups */}
          <div className="sd-overlays-body">
            {overlayGroups.map((group, gi) => (
              <div key={group.label} className="sd-overlay-group">
                <h4 className="sd-overlay-group__title">{group.label}</h4>
                <div className="sd-overlay-group__chips">
                  {group.items.map((item) => (
                    <button key={item.internal} type="button" className={`sd-chip${!selectedMatch ? " sd-chip--disabled" : ""}`} onClick={() => handleChipClick(item)} disabled={!selectedMatch}>
                      {item.display}
                    </button>
                  ))}
                </div>
                {gi < overlayGroups.length - 1 && <div className="sd-divider" />}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel */}
        <div className="sd-panel sd-panel--right">
          <h4 className="sd-panel__title">Live / Preview</h4>
          <div className="sd-divider" />

          <div className="sd-preview-area" style={selectedMatch ? { backgroundImage: `url(${stadiumBg})` } : undefined}>
            {iframeSrc ? (
              <div className="sd-preview-area__iframe-wrapper" ref={previewWrapperRef}>
                <iframe
                  src={iframeSrc}
                  title="match-preview"
                  style={{
                    width: "1920px",
                    height: "1080px",
                    transform: `scale(${iframeScale})`,
                    transformOrigin: "0 0",
                    border: "none",
                  }}
                />
              </div>
            ) : (
              <span className="sd-preview-area__label">NO MATCH</span>
            )}
          </div>

          <div className="sd-preview-actions">
            <div className="sd-preview-actions__btns">
              <button type="button" className={`sd-btn sd-btn--preview${previewMode === "preview" ? " sd-btn--active" : ""}`} onClick={() => setPreviewMode("preview")}>
                Preview
              </button>
              <button type="button" className={`sd-btn sd-btn--live${previewMode === "live" ? " sd-btn--active" : ""}`} onClick={handleGoLive}>
                Go Live
              </button>
              <button type="button" className="sd-btn sd-btn--clear" disabled={!selectedMatch} onClick={() => postGraphics({ graphicsType: "ScoreUpdate" })}>
                Clear Overlays
              </button>
              <button
                type="button"
                className="sd-btn sd-btn--copy"
                disabled={!selectedMatch}
                onClick={() => {
                  if (!selectedMatch) return;
                  const liveUrl = `${import.meta.env.VITE_TICKER_BASE_URL}match/${selectedMatch.matchId}`;
                  navigator.clipboard.writeText(liveUrl);
                }}
              >
                <MdContentCopy /> Copy Live Ticker Link
              </button>
            </div>
            <p className="sd-preview-actions__theme">
              <strong>Theme : </strong>
              <span>{selectedTheme?.name ?? "Default"}</span>
            </p>
          </div>
          {/* 
          <div className="sd-youtube-row">
            <input
              type="text"
              placeholder="Paste youtube link (option)"
              value={youtubeLink}
              onChange={e => setYoutubeLink(e.target.value)}
              className="sd-youtube-row__input"
            />
            <button type="button" className="sd-youtube-row__btn">Add Link</button>
          </div> */}
        </div>
      </div>

      {/* ── Player Stats Dialog ── */}
      {playerStatsDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setPlayerStatsDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">{playerStatsCurrentMatch ? "Match Player Stats" : "Player Stats"}</h3>
            <div className="sd-dialog__tabs">
              <button type="button" className={`sd-dialog__tab${playerStatsTab === "batting" ? " sd-dialog__tab--active" : ""}`} onClick={() => setPlayerStatsTab("batting")}>
                Batting Team
              </button>
              <button type="button" className={`sd-dialog__tab${playerStatsTab === "bowling" ? " sd-dialog__tab--active" : ""}`} onClick={() => setPlayerStatsTab("bowling")}>
                Bowling Team
              </button>
            </div>
            <div className="sd-dialog__list">
              {currentPlayers.length === 0 && <p className="sd-dialog__empty">Loading players...</p>}
              {currentPlayers.map((player, i) => (
                <button
                  key={player.playerId ?? i}
                  type="button"
                  className="sd-dialog__list-item"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: getPlayerStatsType(playerStatsTab),
                      playerId: player.playerId,
                      inningId: latestInning?.inningId,
                    });
                    setPlayerStatsDialogOpen(false);
                  }}
                >
                  {i + 1}. {player.playerFirstName} {player.playerLastName ?? ""}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Scorecard Inning Dialog ── */}
      {scorecardDialogOpen &&
        (() => {
          const innings = matchDetails?.inningScorecardList ?? [];
          const teamName = (inn: InningScorecard | undefined) => (
            scorecardType === "BowlingScoreCard" ||
            scorecardType == "ExtrasChart" ||
            scorecardType == "BowlerVsBowlerChart" ?
            inn?.bowlingTeamName :
            inn?.battingTeamName
          );
          return (
            <div className="sd-dialog-overlay" onClick={() => setScorecardDialogOpen(false)}>
              <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
                <h3 className="sd-dialog__title">Select Inning</h3>
                <div className="sd-dialog__inning-options">
                  {innings.map((inning, index) => {
                    const isCurrent = index === innings.length - 1;
                    return (
                      <button
                        key={inning?.inningId ?? index}
                        type="button"
                        className={`sd-dialog__inning-card ${isCurrent ? "sd-dialog__inning-card--current" : ""}`}
                        onClick={async () => {
                          await postGraphics({ graphicsType: scorecardType, inningId: inning?.inningId });
                          setScorecardDialogOpen(false);
                        }}
                      >
                        <span className="sd-dialog__inning-label">{isCurrent ? "Current Inning" : `Inning ${index + 1}`}</span>
                        <span className="sd-dialog__inning-team">{teamName(inning)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── Player Out Dialog ── */}
      {playerOutDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setPlayerOutDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Select Out Batsman</h3>
            <div className="sd-dialog__list">
              {outBatsmen.length === 0 && <p className="sd-dialog__empty">No batsmen are out in the current inning</p>}
              {outBatsmen.map((batsman, i) => (
                <button
                  key={batsman.playerId ?? i}
                  type="button"
                  className="sd-dialog__list-item"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: "PlayerOut",
                      playerId: batsman.playerId,
                      inningId: latestInning?.inningId,
                    });
                    setPlayerOutDialogOpen(false);
                  }}
                >
                  <div>
                    {batsman.battingOrder}. {batsman.playerFirstName} {batsman.playerLastName ?? ""}
                  </div>
                  <div className="sd-dialog__list-item-sub">
                    Runs: {batsman.run ?? 0} · Balls: {batsman.balls ?? 0}
                    {batsman.wicket?.type ? ` · ${batsman.outString}` : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Match Message Dialog ── */}
      {messageDialogOpen && (
        <div
          className="sd-dialog-overlay"
          onClick={() => {
            setMessageDialogOpen(false);
            setMessageText("");
          }}
        >
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Match Status Message</h3>
            <textarea className="sd-dialog__textarea" placeholder="Type your message here..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
            <div className="sd-dialog__actions">
              <button
                type="button"
                className="sd-dialog__btn-cancel"
                onClick={() => {
                  setMessageDialogOpen(false);
                  setMessageText("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sd-dialog__btn-confirm"
                onClick={async () => {
                  if (!messageText.trim()) return;
                  await postGraphics({ graphicsType: "HeaderMessage", message: messageText.trim() });
                  setMessageDialogOpen(false);
                  setMessageText("");
                }}
              >
                Display
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Squad Details Team Selection Dialog (bca-wpl only) ── */}
      {squadDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setSquadDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">Squad Details — Select Team</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setSquadDialogOpen(false)}>✕</button>
            </div>
            <div className="sd-dialog__team-options">
              {[selectedMatch?.teamOne, selectedMatch?.teamTwo].map((team, i) => (
                <button
                  key={team?.teamSnapshotId ?? i}
                  type="button"
                  className="sd-dialog__team-card"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: "PlayingSquad",
                      teamName: team?.teamName,
                      teamSnapShotId: team?.teamSnapshotId,
                    });
                    setSquadDialogOpen(false);
                  }}
                >
                  {team?.teamName ?? `Team ${i + 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Review Taken Dialog ── */}
      {reviewTakenDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setReviewTakenDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">Review Taken — Select Team</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setReviewTakenDialogOpen(false)}>✕</button>
            </div>
            <div className="sd-dialog__team-options">
              {[selectedMatch?.teamOne, selectedMatch?.teamTwo].map((team, i) => (
                <button
                  key={team?.teamSnapshotId ?? i}
                  type="button"
                  className="sd-dialog__team-card"
                  onClick={async () => {
                    await postGraphics({ graphicsType: "ReviewTaken", teamName: team?.teamName });
                    setReviewTakenDialogOpen(false);
                  }}
                >
                  {team?.teamName ?? `Team ${i + 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Break Duration Dialog (Strategic Timeout / Innings Break) ── */}
      {breakDurationDialogOpen && (
        <div
          className="sd-dialog-overlay"
          onClick={() => setBreakDurationDialogOpen(false)}
        >
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">
              {breakDurationType === "StrategicTimeout" ? "Strategic Timeout" : breakDurationType === "DrsTimer" ? "DRS Timer" : "Innings Break"}
            </h3>
            <p className="sd-dialog__label">Enter break duration</p>
            <div className="sd-dialog__duration-row">
              <div className="sd-dialog__duration-field">
                <input
                  type="number"
                  className="sd-dialog__duration-input"
                  min={0}
                  value={breakDurationMinutes}
                  onChange={(e) => setBreakDurationMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                />
                <span className="sd-dialog__duration-unit">min</span>
              </div>
              <div className="sd-dialog__duration-field">
                <input
                  type="number"
                  className="sd-dialog__duration-input"
                  min={0}
                  max={59}
                  value={breakDurationSeconds}
                  onChange={(e) => setBreakDurationSeconds(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                />
                <span className="sd-dialog__duration-unit">sec</span>
              </div>
            </div>
            <div className="sd-dialog__actions">
              <button
                type="button"
                className="sd-dialog__btn-cancel"
                onClick={() => setBreakDurationDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sd-dialog__btn-confirm"
                onClick={async () => {
                  const totalSeconds = breakDurationMinutes * 60 + breakDurationSeconds;
                  await postGraphics({ graphicsType: breakDurationType, durationInSeconds: totalSeconds });
                  setBreakDurationDialogOpen(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Theme Change Confirmation Dialog (Go Live) ── */}
      {themeChangeConfirmOpen && (
        <div className="sd-dialog-overlay" onClick={handleCancelThemeChangeForLive}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Change Theme in Live Mode?</h3>
            <p className="sd-dialog__label">
              You changed the theme to "{selectedTheme?.name}" in preview mode. Do you want to apply this theme to the live ticker as well?
            </p>
            <div className="sd-dialog__actions">
              <button type="button" className="sd-dialog__btn-cancel" onClick={handleCancelThemeChangeForLive}>
                No
              </button>
              <button type="button" className="sd-dialog__btn-confirm" onClick={handleConfirmThemeChangeForLive}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Wicket Type Dialog ── */}
      {wicketDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setWicketDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">Select Wicket Type</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setWicketDialogOpen(false)}>✕</button>
            </div>
            <div className="sd-dialog__team-options">
              {["RUN OUT", "CAUGHT", "BOWLED", "LBW", "STUMPED"].map((type) => (
                <button
                  key={type}
                  type="button"
                  className="sd-dialog__team-card"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: "Wicket",
                      wicketType: type,
                    });
                    setWicketDialogOpen(false);
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Points Table Dialog ── */}
      {pointsTableDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setPointsTableDialogOpen(false)}>
          <div className="sd-dialog sd-dialog--wide" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Points Table</h3>
            {childDivisions.length === 0 ? (
              <p className="sd-dialog__empty">{tournamentId ? "Loading groups..." : "No tournament info available"}</p>
            ) : (
              <>
                <div className="sd-dialog__tabs">
                  {childDivisions.map((div, i) => (
                    <button key={div.divisionId ?? i} type="button" className={`sd-dialog__tab${pointsTableTab === i ? " sd-dialog__tab--active" : ""}`} onClick={() => setPointsTableTab(i)}>
                      {div.divisionName ?? `Group ${i + 1}`}
                    </button>
                  ))}
                </div>
                <div className="sd-dialog__list">
                  {(childDivisions[pointsTableTab]?.teams ?? []).map((team, i) => (
                    <div key={team.teamSnapshotId ?? i} className="sd-dialog__pt-row">
                      <span className="sd-dialog__pt-rank">{i + 1}</span>
                      <span className="sd-dialog__pt-team">{team.teamName ?? "-"}</span>
                    </div>
                  ))}
                </div>
                <div className="sd-dialog__actions">
                  <button type="button" className="sd-dialog__btn-cancel" onClick={() => setPointsTableDialogOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sd-dialog__btn-confirm"
                    onClick={async () => {
                      await postGraphics({
                        graphicsType: "PointsTable",
                        divisionId: childDivisions[pointsTableTab]?.divisionId,
                      });
                      setPointsTableDialogOpen(false);
                    }}
                  >
                    Display
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamerDashboard;
